import { useEffect, useState } from "react"
import Input, { required, submitForm, useTextInput } from "../../../../components/input"
import MTable from "../../../../components/mtable"
import { api } from "../../../../env"
import { useNavigate } from "react-router-dom"
import { clearCreateProgram, clearStorage, getStorage, setStorage, STORAGE } from "../../../../utils/storage"

const CCP_STEP = {
    META: 0,
    TRACKS: 1,
    QUESTIONNAIRE : 2,
    REVIEW: 3
}

export default function CreateCompanyProgram({companyId, close, questionnaireBasePath = '/admin/questionnaires'}) {

    const navigate = useNavigate()

    const [step, setStep] = useState(CCP_STEP.META)
    const [data, setData] = useState({})

    useEffect(() => {
        let savedStep = getStorage(STORAGE.CREATE_PROGRAM_STEP)
        let savedData = getStorage(STORAGE.CREATE_PROGRAM_DATA)
        if (savedStep && savedData) {
            setStep(savedStep)
            setData(savedData)
        }
        if (savedStep || savedData) clearCreateProgram()
    }, [])

    useEffect(() => {
        console.log("Data or Step changed:")
        console.log(step)
        console.log(data)
    }, [data, step])

    const handleOnNext = (newData) => {
        setData(it => { return {...it, ...newData}})
        setStep(it => it + 1)
    }

    const handleOnConfirm = () => {
        const {trackMetas, questionnaireMeta, ...request} = data
        api.post('programs', {...request, companyId})
            .then(res => {
                console.log(res)
                close()
            })
            .catch(err => close())
    }

    const navigateWithStateSaving = (path) => {
        setStorage(STORAGE.CREATE_PROGRAM_STEP, step)
        setStorage(STORAGE.CREATE_PROGRAM_DATA, data)
        navigate(path)
    }

    return(
        <>
            {step == CCP_STEP.META && <Meta onNext={handleOnNext} onCancel={close}/>}
            {step == CCP_STEP.TRACKS && <Tracks onNext={handleOnNext} onExploreTrack={navigateWithStateSaving} onCancel={close}/>}
            {step == CCP_STEP.QUESTIONNAIRE && <Questionnaire onNext={handleOnNext} label={'Questionnaire'} field={'questionnaire'} onExploreQuestionnaire={navigateWithStateSaving} questionnaireBasePath={questionnaireBasePath} onCancel={close}/>}
            {step == CCP_STEP.REVIEW && <Review onConfirm={handleOnConfirm} onCancel={close} program={data} onExplore={navigateWithStateSaving} questionnaireBasePath={questionnaireBasePath}/>}
        </>
    )
}

function Meta({onNext, onCancel}) {

    const metaForm = {
        name: useTextInput("Name", "label", [required])
    }

    const handleNext = () => {
        const metaInfo = submitForm(metaForm)
        if (!metaInfo) return
        onNext(metaInfo)
    }

    return(<>
        <div className="flex space-between center v-spacer-xs">
            <p className="section-title-s neutral">Basic Information</p> 
            <div className="flex gap-xs">
                <button className="outline" onClick={onCancel}>Cancel</button>
                <button className="solid" onClick={handleNext}>Next Step</button> 
            </div>
        </div>
        <div className="card">
            <Input state={metaForm.name} />
        </div>
    </>)
}

function Tracks({onNext, onExploreTrack, onCancel}) {

    const [selected, setSelected] = useState([])
    const [error, setError] = useState(undefined)

    useEffect(() => {
        let savedSelected = getStorage(STORAGE.CREATE_PROGRAM_TEMP)
        if (savedSelected) {
            setSelected(savedSelected)
            clearStorage([STORAGE.CREATE_PROGRAM_TEMP])
        }
    }, [])

    const tracksMapper = {columns: [
        {
            label: "Name",
            value: (item) => item.name
        },
        {
            label: "Display Name",
            value: (item) => item.displayName
        },
        {
            label: "Status",
            value: (item) => item.status
        },
        {
            label: "",
            actions: [
                {
                    label: "Explore Track",
                    style: (_) => 'outline',
                    onClick: (item) => {
                        setStorage(STORAGE.CREATE_PROGRAM_TEMP, selected)
                        onExploreTrack(`track/${item.id}`)
                    }
                }
            ] 
        }
    ]}

    const handleTrackSelected = (selection) => setSelected(selection)

    const handleNext = () => {
        if (selected.length == 0) setError("Please select at least 1 track.")
        else onNext({
            "trackIds": selected.map(it => it.id),
            "trackMetas": selected
        })   
    }

    return(<>
        <div className="flex space-between center">
            <p className="section-title-s neutral">Tracks</p>
            <div className="flex gap-xs">
                <button className="outline" onClick={onCancel}>Cancel</button>
                <button className="solid" onClick={handleNext}>Next Step</button> 
            </div>
        </div>
        {error && <p className="error showing bold hi-spacer-xxs">{error}</p>}
        <div className="v-spacer-xs"></div>
        <MTable path={'tracks'} queryParams={{'status' : 'Published'}} mapper={tracksMapper} selectable={{onChange: handleTrackSelected, multiple: true, preselected: selected}}/>
    </>)
}

function Questionnaire({onNext, field, label, onExploreQuestionnaire, questionnaireBasePath, onCancel}) {

    const [selected, setSelected] = useState([])
    const [error, setError] = useState(undefined)

    useEffect(() => {
        let savedSelected = getStorage(STORAGE.CREATE_PROGRAM_TEMP)
        if (savedSelected) {
            setSelected(savedSelected)
            clearStorage([STORAGE.CREATE_PROGRAM_TEMP])
        }
    }, [])

    const questioneersMapper = {columns: [
        {
            label: "Name",
            value: (item) => item.name
        },
        {
            label: "Questions Count",
            value: (item) => item.questionsCount
        },
        {
            label: "",
            actions: [
                {
                    label: "Explore Questionnaire",
                    style: (_) => 'outline',
                    onClick: (item) => {
                        setStorage(STORAGE.CREATE_PROGRAM_TEMP, selected)
                        onExploreQuestionnaire(`${questionnaireBasePath}/${item.id}`)
                    }
                }
            ] 
        }

    ]}

    const handleQuestioneerSelected = (selection) => setSelected(selection)

    const handleNext = () => {
        if (selected.length == 0) onNext({
            [`${field}Id`]: null,
            [`${field}Meta`]: null,
            'hasQuestionnaire': false
        })
        else onNext({
            [`${field}Id`]: selected[0].id,
            [`${field}Meta`]: selected[0]
        })   
    }

    return(<>
        <div className="flex space-between center">
            <p className="section-title-s neutral">{label}</p> 
            <div className="flex gap-xs">
                <button className="outline" onClick={onCancel}>Cancel</button>
                <button className="solid" onClick={handleNext}>Next Step</button> 
            </div>
        </div>
        <p className="showing bold hi-spacer-xxs">You can skip the questionnaire in which case the program won't have auto-matching feature enabled.</p>
        <div className="v-spacer-xs"></div>
        <MTable path={'questionnaires'} queryParams={{'status' : 'Published'}} mapper={questioneersMapper} selectable={{onChange: handleQuestioneerSelected, multiple: false, preselected: selected}}/>
    </>)
}

function Review({onConfirm, onCancel, program, onExplore, questionnaireBasePath}) {
    return(<>
        <div className="flex center space-between gap-xs wrap v-spacer-xs">
            <p className="section-title-s neutral">Review</p>
            <button className="outline" onClick={onCancel}>Cancel</button>
        </div>
        <div className="card">
            <div className="flex space-between center v-spacer-l">
                <p className="card-title">Please confirm the program creation.</p>
                <button className="solid-accent" onClick={onConfirm}>Create Program</button>
            </div>
            <p className="section-title-s v-spacer-xs">Basic Information</p>
            <div className="data-grid v-spacer-l">
                <p className="key-item">Name</p>
                <p className="value-item">{program.name}</p>
            </div>
            <p className="section-title-s v-spacer-xs">Questionnaire</p>
            {!program.questionnaireMeta && <p className="card-body v-spacer-l">This program won't use questionnaire & automatic matching.</p>}
            {program.questionnaireMeta && <div className="dashed-card justify-start gap-s columns padding-s v-spacer-l showing clickable" onClick={() => onExplore(`${questionnaireBasePath}/${program.questionnaireMeta.id}`)}>
                <p className="card-title">{program.questionnaireMeta.name}</p>
                <p className="card-label uppercase neutral">Click To Explore</p>
            </div>}
            <p className="section-title-s v-spacer-xs">Tracks</p>
            <div className="grid gap-xs v-spacer-l" style={{gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))'}}>
                {program.trackMetas.map(track => <div className="dashed-card justify-start gap-s columns padding-s showing clickable" onClick={() => onExplore(`track/${track.id}`)}>
                    <p className="card-title">{track.displayName}</p>
                    <p className="card-body">{track.description}</p>
                    <p className="card-label uppercase neutral">Click To Explore</p>
                </div>)}
            </div>
        </div>
    </>)
}